import * as Sentry from '@sentry/react';
import {analyticsLogger} from 'analytics/analyticsLogger';
import {AxiosError} from 'axios';
import {ApiError} from 'client';
import {CancelError} from 'client/core/CancelablePromise';

// =============================================================================
// Sentry Error Classification
// =============================================================================

/**
 * Actions that can be taken for errors sent to Sentry:
 * - 'drop': Don't send to Sentry at all
 * - 'warning': Send as a warning message (captureMessage)
 * - 'error': Send as an error exception (captureException) - default
 */
export type SentryAction = 'drop' | 'warning' | 'error';

function shouldWarnForNetworkError(status: any, response: any, url: string): boolean {
  // If WAF/ACL returns 403/429 response, return true to alert team to investigate.
  if (status === 403 || status === 429) return true;
  // If ERR_NETWORK has no response, this could still be WAF/ACL blocking requests.
  // Return true to trigger warning for important endpoints (e.g. commenting, updating workstreams, etc).
  if (!response) {
    const shouldWarn =
      url.includes('/comment') ||
      url.includes('/worsktream') ||
      url.includes('/editordoc') ||
      url.includes('/context-engine/chat');
    if (shouldWarn) return true;
  }
  return false;
}

/**
 * Classify an error and determine what action Sentry should take.
 * This is the single source of truth for error classification.
 */
export function classifySentryError(error: any): SentryAction {
  if (!error) return 'error';

  const message = (error?.message || '').toLowerCase();
  const code = error?.code;
  const name = error?.name;
  const response = error?.response;
  const status = response?.status || error?.status;
  const url = error?.url || error?.config?.url || '';

  // -------------------------------------------------------------------------
  // DROP: Errors that should not be sent to Sentry at all
  // These are user-initiated or expected transient errors, not bugs
  // -------------------------------------------------------------------------

  // User cancelled the request (navigation, component unmount)
  if (error instanceof CancelError || name === 'CancelError') return 'drop';
  if (code === 'ERR_CANCELED' || message === 'canceled') return 'drop';
  if (name === 'AbortError') return 'drop';
  if (message.includes('request aborted') || message.includes('aborted a request')) return 'drop';

  // Network/connection errors (user's internet, not our bug)
  // EXCEPT: 403/429 errors which may indicate WAF/ACL/rate limiting blocking legitimate users
  if (code === 'ECONNABORTED' || code === 'ERR_NETWORK' || code === 'ETIMEDOUT') {
    // If this causes too much noise, we can re-assess and remove this check.
    if (shouldWarnForNetworkError(status, response, url)) return 'warning';
    return 'drop';
  }
  if (message.includes('network error') || message.includes('network request failed')) {
    // If this causes too much noise, we can reassess and remove this check.
    if (shouldWarnForNetworkError(status, response, url)) return 'warning';
    return 'drop';
  }
  if (message.includes('timeout of') && message.includes('exceeded')) return 'drop'; // Axios timeout

  // Benign browser warnings
  if (message.includes('resizeobserver loop')) return 'drop';

  // -------------------------------------------------------------------------
  // WARNING: Errors that should be logged but aren't critical bugs
  // These are user-caused issues or expected edge cases
  // -------------------------------------------------------------------------

  // User navigated to an invalid URL
  if (message.includes('invalid url')) return 'warning';

  // -------------------------------------------------------------------------
  // ERROR: Everything else is a real error
  // -------------------------------------------------------------------------
  return 'error';
}

/**
 * Check if an error should be completely filtered from Sentry.
 * Convenience wrapper for use in beforeSend and other filters.
 */
export function shouldFilterFromSentry(error: any): boolean {
  return classifySentryError(error) === 'drop';
}

// =============================================================================
// API Error Logging
// =============================================================================

interface LogContext {
  component?: string;
  userAction?: string;
  url?: string;
  method?: string;
}

/**
 * Log an API error to analytics and Sentry.
 * - 5xx server errors: logged as Sentry exceptions (bugs on our end)
 * - 4xx client errors: logged as Sentry warnings (expected behavior)
 */
export function logApiError(error: ApiError, context?: LogContext) {
  if (shouldFilterFromSentry(error)) {
    return;
  }

  const status = error.status;
  const url = error.request.url;
  const errorInfo = {
    error: error.message,
    status,
    statusText: error.statusText,
    url,
    body: error.body,
    method: error.request.method,
    component: context?.component,
    userAction: context?.userAction,
    timestamp: new Date().toISOString(),
  };

  // Always log to analytics
  analyticsLogger().logEvent('API_FRONTEND_API_ERROR', errorInfo);

  // Log to Sentry with appropriate severity
  const tags = {
    error_type: `${status}_api_error`,
    component: context?.component || 'unknown',
  };

  if (status >= 500) {
    // 5xx = server bug, log as exception with stack trace
    Sentry.captureException(error, {tags, extra: errorInfo});
  } else {
    // 4xx = client error, log as warning message
    Sentry.captureMessage(`${status} ${error.message}: ${url}`, {
      level: 'warning',
      tags,
      extra: errorInfo,
    });
  }
}

export function logAxiosError(error: AxiosError, context?: LogContext) {
  if (shouldFilterFromSentry(error)) {
    return;
  }

  const status = error.response?.status || error.status;
  const url = error.config?.url ?? '';
  const errorInfo = {
    error: error.message,
    status,
    statusText: error.response?.statusText,
    url,
    body: error?.config?.data,
    method: error.request.method,
    component: context?.component,
    userAction: context?.userAction,
    timestamp: new Date().toISOString(),
  };

  // Always log to analytics
  analyticsLogger().logEvent('API_FRONTEND_API_ERROR', errorInfo);

  // Log to Sentry with appropriate severity
  const tags = {
    error_type: `${status}_axios_error`,
    component: context?.component || 'unknown',
  };

  if (status && status >= 500) {
    // 5xx = server bug, log as exception with stack trace
    Sentry.captureException(error, {tags, extra: errorInfo});
  } else {
    // 4xx = client error, log as warning message
    Sentry.captureMessage(`${status} ${error.message}: ${url}`, {
      level: 'warning',
      tags,
      extra: errorInfo,
    });
  }
}

/**
 * Check if an error is a 5xx server error.
 */
export function is5xxError(error: any): boolean {
  const status = error?.status || error?.response?.status;
  return status >= 500 && status < 600;
}

/**
 * Log an error to analytics and console. Does not send to Sentry.
 * Use for errors that are already handled or don't need alerting.
 */
export function reportAndLogError(error: any, message?: string) {
  const errorInfo = {
    error: error instanceof Error ? error.message : String(error),
    status: error?.status || error?.response?.status,
    url: error?.url || error?.config?.url,
    page: window.location.pathname,
    timestamp: new Date().toISOString(),
  };

  analyticsLogger().logEvent('ERROR_PAGE', errorInfo);
  console.error(message || 'Error:', error);
}

/**
 * Log a 5xx server error. Use logApiError for ApiError instances.
 */
export function log5xxError(error: any, context?: LogContext) {
  if (shouldFilterFromSentry(error)) {
    return;
  }

  const status = error?.status || error?.response?.status;
  const errorInfo = {
    error: error instanceof Error ? error.message : String(error),
    status,
    url: context?.url || error?.url || error?.config?.url,
    method: context?.method || error?.config?.method,
    component: context?.component,
    userAction: context?.userAction,
    timestamp: new Date().toISOString(),
  };

  analyticsLogger().logEvent('API_FRONTEND_5XX', errorInfo);

  Sentry.captureException(error, {
    tags: {
      error_type: `${status}_server_error`,
      component: context?.component || 'unknown',
    },
    extra: errorInfo,
  });
}
